import React, { useState } from "react";
import { Card } from "@rumbledirect/ui";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

interface AntdPieChartProps {
  title: string;
  data: Array<{ name: string; value: number }>;
  className?: string;
}

const COLORS = [
  "#8B0000", // Dark Red
  "#B22222", // Fire Brick Red
  "#DC143C", // Crimson
  "#FF0000", // Pure Red
  "#FF4500", // Orange Red
  "#FF6600", // Vivid Orange Red
  "#FF7F00", // Dark Orange
  "#FF8C00", // Dark Orange 2
  "#FFA500", // Orange
  "#FFB347", // Peach Orange
  "#FFC0CB", // Light Orange
  "#FFD700", // Gold
  "#A52A2A", // Brown Red
  "#CD5C5C", // Indian Red
  "#F08080", // Light Coral
  "#FA8072", // Salmon
  "#FF69B4", // Hot Pink Red
  "#FF1493", // Deep Pink Red
  "#B8860B", // Dark Goldenrod
  "#DAA520", // Goldenrod
  "#FF7F50", // Coral
  "#FF6347", // Tomato
  "#FF4040", // Bright Red
  "#D2691E", // Chocolate Orange
];

export const AntdPieChart = ({ title, data, className }: AntdPieChartProps) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  // Process data to limit categories and group others
  const processedData = React.useMemo(() => {
    return data;
  }, [data]);

  const onPieEnter = (_: any, index: number) => {
    setActiveIndex(index);
  };

  const onPieLeave = () => {
    setActiveIndex(null);
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <div className="bg-white p-2 border border-gray-300 rounded shadow-lg">
          <p className="font-medium">{data.payload.name}</p>
          <p className="text-blue-600">
            Aantal:{" "}
            <span className="font-bold">{data.value.toLocaleString()}</span>
          </p>
          <p className="text-gray-600">
            Percentage:{" "}
            <span className="font-bold">
              {(
                (data.value /
                  processedData.reduce((sum, item) => sum + item.value, 0)) *
                100
              ).toFixed(1)}
              %
            </span>
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <Card className={className} title={title}>
      <ResponsiveContainer width="100%" height={250}>
        <PieChart>
          <Pie
            data={processedData}
            cx="50%"
            cy="50%"
            outerRadius={80}
            fill="#8884d8"
            dataKey="value"
            nameKey="name"
            onMouseEnter={onPieEnter}
            onMouseLeave={onPieLeave}
          >
            {processedData.map((_entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={COLORS[index % COLORS.length]}
                stroke={activeIndex === index ? "#333" : "none"}
                strokeWidth={activeIndex === index ? 2 : 0}
                style={{
                  filter:
                    activeIndex !== null && activeIndex !== index
                      ? "opacity(0.6)"
                      : "opacity(1)",
                  cursor: "pointer",
                }}
              />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
        </PieChart>
      </ResponsiveContainer>
    </Card>
  );
};
